import { isWithinInterval, setHours, setMinutes } from 'date-fns';
import React, { useCallback, useEffect, useRef } from 'react';
import { waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Button, Colors, Prompt } from '../../../ui';
import { StarRating } from '../../../ui/StarRating';
import { useResolve } from '../../Container';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { confettiScreen } from '../../onboarding/OnboardingCommands';
import { Popup, PopupProps } from '../../popups/Popup';
import { PopupManager } from '../../popups/PopupManager';
import { meetingFeedbackPopup } from './MeetingFeedbackPopup';

type TimeOfDay = 'morning' | 'lunch' | 'coffee' | 'home' | 'other';

const bracket = (tod: TimeOfDay, interval: Interval) => [tod, interval] as const;

const computeTimeOfDate = (date: Date = new Date()): TimeOfDay => {
    const blocks = [
        bracket('morning', {
            start: setHours(setMinutes(date, 0), 8),   // 8:00 AM
            end: setHours(setMinutes(date, 30), 11)    // 11:30 AM
        }),
        bracket('lunch', {
            start: setHours(setMinutes(date, 30), 11), // 11:30 AM
            end: setHours(setMinutes(date, 30), 13)    // 1:30 PM
        }),
        bracket('coffee', {
            start: setHours(setMinutes(date, 30), 13), // 1:30 PM
            end: setHours(setMinutes(date, 0), 17)     // 5:00 PM
        }),
        bracket('home', {
            start: setHours(setMinutes(date, 0), 17),  // 5:00 PM
            end: setHours(setMinutes(date, 0), 19)     // 7:00 PM
        }),
    ];

    const [block] = blocks.find(([_, interval]) => isWithinInterval(date, interval)) ?? ['other'];
    return block;
};

const TimeBasedMessage = () => {
    const end = 'how did you find using Metro Retro today?';
    switch (computeTimeOfDate()) {
        case 'morning':
            return <span>{`Maybe it's time for morning coffee? But before you go, ${end}`}</span>;
        case 'lunch':
            return <span>{`Just in time for lunch maybe? But before you go, ${end}`}</span>;
        case 'coffee':
            return <span>{`Time for a cheeky afternoon coffee maybe? But before you go, ${end}`}</span>;
        case 'home':
            return <span>{`Looks like it's nearly time to go home! But before you go, ${end}`}</span>;
        default:
            return <span>{`You're free to carry on the rest of your day... But before you do, ${end}`}</span>;
    }
};

export const meetingFinishedPopup = () => ({ ui: MeetingFinishedPopup, fade: false });

export const MeetingFinishedPopup: LFC<PopupProps> = ({ dismiss }) => {

    const dispatch = useResolve(CommandDispatcher);
    const popups = useResolve(PopupManager);
    const base = useRef<HTMLDivElement>();

    const pop = () => {
        if (!base.current) return;
        const { left, top, width, height } = base.current.getBoundingClientRect();

        for (let angle = 30; angle <= 150; angle += 30) {
            dispatch(confettiScreen, [
                (left + (width / 2)) / window.innerWidth,
                (top + (height / 2)) / window.innerHeight,
                angle
            ]);
        }
    };

    useEffect(() => void (waitFor(100).then(pop)), [base.current]);

    const onFeedback = useCallback((rating: number) => {
        popups.invoke(meetingFeedbackPopup(rating));
    }, []);

    return <Prompt ignoreEnter onAction={dismiss}>
        <Popup ref={base} w="34" p="0" shadow="lg" style={{ overflow: 'hidden' }}>
            <Flexer flex="column" center gap="1" padding="2" background={Colors.positive}>
                <Flexer flex="column" gap=".5" style={{ textAlign: 'center' }}>
                    <Title flush size="xl" color="white">The meeting is now over!</Title>
                    <Copy flush size="1.125" color="white">
                        <TimeBasedMessage />
                    </Copy>
                </Flexer>
                <StarRating size={5} onSelect={onFeedback} />
                <Popup.Actions style={{ color: 'white' }}>
                    <Button onAction={dismiss}>Close</Button>
                </Popup.Actions>
            </Flexer>
        </Popup>
    </Prompt>;
};
